import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip leggero per la sidebar ridotta (e ovunque serva).
 * Renderizzato in un PORTALE a document.body così non viene mai clippato
 * dal layout circostante (overflow, sticky, transform...).
 *
 * Props:
 *   - label: testo da mostrare
 *   - side: 'right' (default) | 'top' | 'bottom' | 'left'
 *   - disabled: se true, non mostra mai
 *   - delay: ms (default 150)
 *   - children: elemento che riceve hover/focus
 */
const FADE_MS = 180;
const LONGPRESS_MS = 450;
const OFFSET = 8; // gap tra trigger e tooltip

function isReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function Tooltip({ label, side = 'right', disabled = false, delay = 150, wide = false, maxWidth = 280, children }) {
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const hoverT = useRef(null);
  const longPressT = useRef(null);

  // Mount/unmount con coda per fade-out
  useEffect(() => {
    if (show) {
      setMounted(true);
      return;
    }
    const t = setTimeout(() => setMounted(false), FADE_MS);
    return () => clearTimeout(t);
  }, [show]);

  // Posizionamento dinamico relativo al trigger
  useEffect(() => {
    if (!mounted || !show) return;
    if (!wrapRef.current) return;
    const triggerRect = wrapRef.current.getBoundingClientRect();
    const tipRect = tipRef.current?.getBoundingClientRect() || { width: 120, height: 24 };
    let top = 0;
    let left = 0;
    switch (side) {
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tipRect.height / 2;
        left = triggerRect.left - tipRect.width - OFFSET;
        break;
      case 'top':
        top = triggerRect.top - tipRect.height - OFFSET;
        left = triggerRect.left + triggerRect.width / 2 - tipRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + OFFSET;
        left = triggerRect.left + triggerRect.width / 2 - tipRect.width / 2;
        break;
      case 'right':
      default:
        top = triggerRect.top + triggerRect.height / 2 - tipRect.height / 2;
        left = triggerRect.right + OFFSET;
        break;
    }
    const margin = 4;
    top = Math.max(margin, Math.min(top, window.innerHeight - tipRect.height - margin));
    left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin));
    setPos({ top, left });
  }, [mounted, show, side, label]);

  if (disabled || !label) return children;

  const open = (immediate = false) => {
    clearTimeout(hoverT.current);
    if (immediate) setShow(true);
    else hoverT.current = setTimeout(() => setShow(true), delay);
  };
  const close = () => {
    clearTimeout(hoverT.current);
    clearTimeout(longPressT.current);
    setShow(false);
  };

  const reduceMotion = isReducedMotion();

  return (
    <>
      <span
        ref={wrapRef}
        className="relative inline-flex"
        onMouseEnter={() => open(false)}
        onMouseLeave={close}
        onFocus={() => open(true)}
        onBlur={close}
        onTouchStart={() => {
          clearTimeout(longPressT.current);
          longPressT.current = setTimeout(() => setShow(true), LONGPRESS_MS);
        }}
        onTouchEnd={close}
        onTouchCancel={close}
      >
        {children}
      </span>
      {mounted && typeof document !== 'undefined' && createPortal(
        <span
          ref={tipRef}
          role="tooltip"
          className={
            'pointer-events-none fixed ' +
            (wide ? 'whitespace-pre-line break-words ' : 'whitespace-nowrap ') +
            'bg-ink text-canvas px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] shadow-md'
          }
          style={{
            top: pos.top,
            left: pos.left,
            zIndex: 9999,
            opacity: show ? 1 : 0,
            maxWidth: wide ? `${maxWidth}px` : undefined,
            transition: reduceMotion ? 'none' : `opacity ${FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
          data-testid="sidebar-tooltip"
        >
          {label}
        </span>,
        document.body,
      )}
    </>
  );
}
